import { useCallback, useEffect, useRef } from "react";

interface UseDraggableOptions {
  dragConstraints?: React.RefObject<Element>;
  dragScale?: number;
}

export function useDraggable<T extends HTMLElement>({
  dragConstraints,
  dragScale = 1,
}: UseDraggableOptions = {}) {
  const ref = useRef<T>(null);
  const translateRef = useRef({ x: 0, y: 0 });
  const hasDraggedRef = useRef(false);
  const activeCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.willChange = "transform";
    const preventDragStart = (e: DragEvent) => e.preventDefault();
    el.addEventListener("dragstart", preventDragStart);
    return () => {
      el.style.willChange = "";
      el.removeEventListener("dragstart", preventDragStart);
      activeCleanupRef.current?.();
    };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<T>) => {
      const el = ref.current;
      if (!el) return;

      hasDraggedRef.current = false;

      const startPointerX = e.clientX;
      const startPointerY = e.clientY;
      const startTranslateX = translateRef.current.x;
      const startTranslateY = translateRef.current.y;

      const elementRect = el.getBoundingClientRect();
      const baseLeft = elementRect.left - startTranslateX;
      const baseTop = elementRect.top - startTranslateY;
      const { width, height } = elementRect;

      // Route all future pointer events to this element until pointerup
      el.setPointerCapture(e.pointerId);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const dx = moveEvent.clientX - startPointerX;
        const dy = moveEvent.clientY - startPointerY;

        if (!hasDraggedRef.current && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
          hasDraggedRef.current = true;
          el.style.transition = "none";
          el.style.cursor = "grabbing";
        }

        if (!hasDraggedRef.current) return;

        const rawX = startTranslateX + dx;
        const rawY = startTranslateY + dy;

        let x = rawX;
        let y = rawY;

        if (dragConstraints?.current) {
          const containerRect = dragConstraints.current.getBoundingClientRect();
          x = Math.max(
            containerRect.left - baseLeft,
            Math.min(containerRect.right - baseLeft - width, rawX),
          );
          y = Math.max(
            containerRect.top - baseTop,
            Math.min(containerRect.bottom - baseTop - height, rawY),
          );
        }

        translateRef.current = { x, y };
        el.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${dragScale})`;
      };

      const cleanup = (cancel = false) => {
        activeCleanupRef.current = null;
        el.style.transition = "transform 0.15s ease-out";
        el.style.cursor = "";
        if (cancel) {
          translateRef.current = { x: startTranslateX, y: startTranslateY };
        }
        el.style.transform = `translate3d(${translateRef.current.x}px, ${translateRef.current.y}px, 0)`;
        el.removeEventListener("pointermove", handlePointerMove);
        el.removeEventListener("pointerup", handlePointerUp);
        el.removeEventListener("pointercancel", handlePointerCancel);
        el.removeEventListener("lostpointercapture", handleLostPointerCapture);
      };

      const handlePointerUp = () => cleanup();
      const handlePointerCancel = () => cleanup(true);
      const handleLostPointerCapture = () => cleanup(true);

      activeCleanupRef.current = () => cleanup(true);
      el.addEventListener("pointermove", handlePointerMove);
      el.addEventListener("pointerup", handlePointerUp);
      el.addEventListener("pointercancel", handlePointerCancel);
      el.addEventListener("lostpointercapture", handleLostPointerCapture);
    },
    [dragConstraints, dragScale],
  );

  const onClickCapture = useCallback((e: React.MouseEvent<T>) => {
    if (hasDraggedRef.current) {
      e.stopPropagation();
      e.preventDefault();
      hasDraggedRef.current = false;
    }
  }, []);

  return { ref, onPointerDown, onClickCapture };
}
